import { Fragment, useState } from "react";

import classnames from "classnames";
import { isNot, hyphenate, isNotEmpty, isPresent } from "neetocist";
import useLocalStorage from "neetocommons/react-utils/useLocalStorage";
import { Right } from "neetoicons";
import { Typography, Tag } from "neetoui";
import { append, is } from "ramda";
import { useLocation } from "react-router-dom";

import AddDropdown from "./AddDropdown";
import CheckPointNavLinks from "./CheckPointNavLink";
import Items from "./Items";
import MoreDropdown from "./MoreDropdown";

import { PINNED_MORE_NAV_LINKS_KEY } from "../../constants";
import { filterByPermissions } from "../../utils";

const renderCount = count => (count > 999 ? "999+" : count);

const Links = ({
  navLinks,
  moreNavLinks,
  isCountsLoading: isCountsLoadingDeprecated,
  setSelectedNavLinkRoute,
}) => {
  const location = useLocation();
  const [expandedNavLinkKeys, setExpandedNavLinkKeys] = useState([]);
  const [pinnedNavLinks, setPinnedNavLinks] = useLocalStorage(
    PINNED_MORE_NAV_LINKS_KEY,
    []
  );

  const handleNavLinkClick = ({ event, isActive, navLink }) => {
    const isExpandable =
      isPresent(navLink.items) || isPresent(navLink.component);
    if (!isExpandable) {
      setExpandedNavLinkKeys([]);

      return;
    }

    if (navLink.isConfigureNavLink && isNotEmpty(navLink.items)) {
      setSelectedNavLinkRoute(navLink.to);
    }

    const navLinkKey = navLink.key || navLink.to;
    if (isActive()) {
      const isExpanded = expandedNavLinkKeys.includes(navLinkKey);
      setExpandedNavLinkKeys(isExpanded ? [] : [navLinkKey]);
      navLink.resetPath !== true && event.preventDefault();

      return;
    }

    setExpandedNavLinkKeys([navLinkKey]);
  };

  const handleTogglePin = (navLink, event) => {
    event.preventDefault();
    event.stopPropagation();
    const navLinkKey = navLink.key || navLink.to;
    const isPinned = pinnedNavLinks.includes(navLinkKey);

    if (isPinned) {
      setPinnedNavLinks(pinnedNavLinks.filter(isNot(navLinkKey)));
    } else {
      setPinnedNavLinks([...pinnedNavLinks, navLinkKey]);
    }
  };

  const isNavLinkPinned = navLink => {
    if (!navLink) return false;
    const navLinkKey = navLink.key || navLink.to;

    return pinnedNavLinks.includes(navLinkKey);
  };

  const pinnedItems = moreNavLinks ? moreNavLinks.filter(isNavLinkPinned) : [];

  const activeMoreNavLink = moreNavLinks
    ? moreNavLinks.find(
        navLink =>
          navLink &&
          (location.pathname + location.search).startsWith(navLink.to)
      )
    : null;

  const additionalItems = [...pinnedItems];

  if (activeMoreNavLink && !isNavLinkPinned(activeMoreNavLink)) {
    additionalItems.push(activeMoreNavLink);
  }

  const combinedNavLinks = [...navLinks, ...additionalItems];

  return (
    <div className="neeto-molecules-sidebar__links neeto-molecules-sidebar__no-shrinkable">
      {combinedNavLinks.filter(filterByPermissions).map((navLink, index) => {
        const {
          label,
          to,
          icon,
          items,
          isConfigureNavLink,
          isCountsLoading = false,
          count = null,
          component: Component,
          onClick,
          addActions = [],
          tag,
          ...otherProps
        } = navLink;
        const IconSVG = icon;
        const url = new URL(to, window.location.href);
        const isActive = () =>
          is(Function, otherProps.isActive)
            ? otherProps.isActive()
            : location.pathname.startsWith(url.pathname);

        const isExpandable = items || Component;
        const navLinkKey = navLink.key || navLink.to;
        const isMenuExpanded =
          expandedNavLinkKeys.includes(navLinkKey) &&
          !isCountsLoadingDeprecated;

        const handleCaretClick = event => {
          event.preventDefault();
          event.stopPropagation();
          setExpandedNavLinkKeys(keys =>
            keys.includes(navLinkKey)
              ? keys.filter(isNot(navLinkKey))
              : append(navLinkKey, keys)
          );
        };

        return (
          <Fragment key={navLink.key || index}>
            <CheckPointNavLinks
              activeClassName="active"
              className="neeto-ui-flex neeto-ui-items-center neeto-ui-no-underline neeto-ui-select-none neeto-molecules-sidebar__link"
              data-testid={`navlink-${hyphenate(label)}`}
              onClick={event => {
                handleNavLinkClick({ event, isActive, navLink });
                onClick?.(event);
              }}
              {...{ isActive, to, ...otherProps }}
            >
              {icon && (
                <span
                  className="neeto-molecules-sidebar__link-icon"
                  data-testid={label}
                >
                  <IconSVG />
                  {isPresent(count) && !isCountsLoading && (
                    <span className="neeto-molecules-sidebar__link-unread-count">
                      {renderCount(count)}
                    </span>
                  )}
                </span>
              )}
              <Typography
                className="neeto-molecules-sidebar__link-label neeto-ui-flex-grow flex items-center gap-2"
                component="span"
                style="body2"
              >
                {label}
                {tag && <Tag {...tag} />}
              </Typography>
              {isNotEmpty(addActions) && <AddDropdown {...{ addActions }} />}
              {isExpandable && !isConfigureNavLink && (
                <span
                  className={classnames(
                    "neeto-molecules-sidebar__link-icon neeto-molecules-sidebar__link-icon--caret",
                    {
                      "neeto-molecules-sidebar__link-icon--rotate":
                        isMenuExpanded,
                    }
                  )}
                  onClick={handleCaretClick}
                >
                  <Right size={16} />
                </span>
              )}
            </CheckPointNavLinks>
            {isExpandable && !isConfigureNavLink && isMenuExpanded && (
              <>
                {items && (
                  <Items {...{ isCountsLoading, items, ...otherProps }} />
                )}
                {Component && <Component {...{ navLink }} />}
              </>
            )}
          </Fragment>
        );
      })}
      {isNotEmpty(moreNavLinks) && (
        <MoreDropdown {...{ handleTogglePin, isNavLinkPinned, moreNavLinks }} />
      )}
    </div>
  );
};

export default Links;
